// Componente para cargar el widget de Zendesk solo para tenant_external (NO para tenant_3pl)
// Zendesk widget component for tenant_external teams only (excluded from tenant_3pl)

import { APP_CONFIG } from '@config/index';
import {
  authenticateZendeskVisitor,
  type ZendeskAuthTrigger,
} from '@shared/components/integrations/zendeskMessagingAuth';
import { useAuthStore } from '@shared/stores/authStore';
import { useEffect } from 'react';

export const ZendeskWidget = () => {
  const getTeamType = useAuthStore((state) => state.getTeamType);
  const user = useAuthStore((state) => state.user);
  const teamType = getTeamType();

  useEffect(() => {
    // Solo cargar el widget para tenant_external (no para tenant ni tenant_3pl)
    if (teamType !== 'tenant_external') {
      return;
    }

    // Verificar si el script ya está cargado
    const existingScript = document.getElementById('ze-snippet');
    if (existingScript) {
      return;
    }

    // Crear y añadir el script de Zendesk
    const script = document.createElement('script');
    script.id = 'ze-snippet';
    script.src = `https://static.zdassets.com/ekr/snippet.js?key=${APP_CONFIG.zendeskWidgetKey}`;
    script.async = true;

    document.head.appendChild(script);

    return () => {
      const scriptToRemove = document.getElementById('ze-snippet');
      if (scriptToRemove) {
        scriptToRemove.remove();
      }

      if (typeof window.zE === 'function') {
        try {
          window.zE('messenger', 'logoutUser');
        } catch {
          // ignore
        }
        try {
          window.zE('webWidget', 'hide');
        } catch {
          try {
            window.zE('messenger', 'hide');
          } catch {
            // ignore
          }
        }
      }
    };
  }, [teamType, APP_CONFIG.zendeskWidgetKey, user?.id]);

  // JWT Messaging si el backend lo expone; si no, identify como antes
  useEffect(() => {
    if (teamType !== 'tenant_external' || !user?.id) {
      return;
    }

    const name = user.name ?? '';
    let cancelled = false;

    const runAuth = (trigger: ZendeskAuthTrigger) => {
      if (cancelled) return;
      void authenticateZendeskVisitor(name, { trigger, userId: user.id });
    };

    if (typeof window.zE === 'function') {
      runAuth('immediate');
      return () => {
        cancelled = true;
      };
    }

    const script = document.getElementById('ze-snippet');
    if (!script) {
      return () => {
        cancelled = true;
      };
    }

    const onLoad = () => runAuth('script_load');
    script.addEventListener('load', onLoad);

    const t = window.setTimeout(() => runAuth('timeout'), 100);

    return () => {
      cancelled = true;
      script.removeEventListener('load', onLoad);
      window.clearTimeout(t);
    };
  }, [teamType, user?.id, user?.name]);

  return null;
};

declare global {
  interface Window {
    zE?: any;
  }
}
